// Updated QRWizard.jsx - Using Context
import React from 'react';
import styled from '@emotion/styled';
import { useQRContext } from '../context/QRContext';
import QRStepIndicator from './QRStepIndicator';
import QRContentStep from './QRContentStep';
import QRCustomizeStep from './QRCustomizeStep';
import QRDownloadStep from './QRDownloadStep';
import QRPreviewPanel from './QRPreviewPanel';
import Header from './Header';

const PageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  padding: 1.5rem 0;
  position: relative;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;

  /* Animated background elements */
  &::before {
    content: '';
    position: absolute;
    top: 10%;
    left: -5%;
    width: 300px;
    height: 300px;
    background: rgba(124, 58, 237, 0.1);
    border-radius: 50%;
    filter: blur(60px);
    animation: float 8s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 10%;
    right: -5%;
    width: 400px;
    height: 400px;
    background: rgba(236, 72, 153, 0.08);
    border-radius: 50%;
    filter: blur(80px);
    animation: float 10s ease-in-out infinite reverse;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const WizardHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding-top: 2rem;
`;

const HeaderTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 900;
  background: linear-gradient(45deg, #ffffff, #e2e8f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  margin-top: 8rem;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeaderSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.125rem;
  margin-bottom: 0;
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 10;
  padding: 0 1rem;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 350px;
    gap: 1.5rem;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    padding: 0 1.5rem;
  }

  @media (max-width: 768px) {
    gap: 2rem;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    gap: 1.5rem;
    padding: 0 0.75rem;
  }
`;

const QRWizard = () => {
  const {
    // State from context
    currentStep,
    contentType,
    formData,
    qrOptions,
    canProceed,
    previewRef,

    // Actions from context
    nextStep,
    previousStep,
    resetAll,
    setContentType,
    updateFormData,
    updateQROptions,
    setCurrentStep,
  } = useQRContext();

  const handleDownloadClick = () => {
    // Navigate to the download step (step 3)
    setCurrentStep(3);
  };

  const renderCurrentStep = () => {
    const stepProps = {
      formData,
      setFormData: updateFormData,
      contentType,
      setContentType,
      qrOptions,
      setQrOptions: updateQROptions,
      onNext: nextStep,
      onBack: previousStep,
      onStartOver: resetAll,
      canProceed: canProceed(),
      currentStep,
      previewRef,
    };

    switch (currentStep) {
      case 1:
        return <QRContentStep {...stepProps} />;
      case 2:
        return <QRCustomizeStep {...stepProps} />;
      case 3:
        return <QRDownloadStep {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <Header />

      <WizardHeader>
        <HeaderTitle>Qraft Your Code</HeaderTitle>
        <HeaderSubtitle>
          Create beautiful, customizable QR codes in seconds
        </HeaderSubtitle>
      </WizardHeader>

      <QRStepIndicator currentStep={currentStep} />

      <MainLayout>
        {renderCurrentStep()}

        <QRPreviewPanel
          ref={previewRef}
          formData={formData}
          contentType={contentType}
          qrOptions={qrOptions}
          canProceed={canProceed()}
          onDownloadClick={handleDownloadClick}
        />
      </MainLayout>
    </PageContainer>
  );
};

export default QRWizard;
